import { useEditor } from "@tiptap/react";
import { bool } from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import StarterKit from "@tiptap/starter-kit";
import { useSignups } from "Signups/state";
import { AnimatePresence, motion } from "framer-motion";

import { EditorTypography } from "UI/FnrTextEditor/EditorTypography";

import removeHtmlTags from "Utilities/removeHtmlTags";

import { useEditDescription } from "../api/queries";

import DescriptionEditor from "./DescriptionEditor";

export default function Description({ lightMode }) {
  const editorRef = useRef(null);
  const [state, dispatch] = useSignups();
  const description = state.currentSignupList.description;
  const signupID = state.currentSignupList.ID;

  const [value, setValue] = useState(description);

  const editor = useEditor({
    content: value,
    onUpdate({ editor }) {
      setValue(editor.getHTML());
    },
    autofocus: false,
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder: "Add a description...",
        emptyEditorClass: "is-editor-empty",
      }),
    ],
  });

  const [originalDescription, setOriginalDescription] = useState(description);
  const [hasSavedOrginalDescription, setHasSavedOriginalDescription] =
    useState(false);

  const saveOriginalDescription = useCallback(() => {
    if (!hasSavedOrginalDescription) {
      setOriginalDescription(description);
      setHasSavedOriginalDescription(true);
    }
  }, [description, hasSavedOrginalDescription]);

  const resetOriginalDescription = useCallback(() => {
    setOriginalDescription(description);
    setHasSavedOriginalDescription(false);
  }, [description]);

  // not sure why, but react query not returning isSuccess properly. So this is a hack around that.
  const [isSuccess, setIsSuccess] = useState(false);
  useEffect(() => {
    if (isSuccess) {
      setTimeout(() => {
        setIsSuccess(false);
      }, 3000);
    }
  }, [isSuccess]);

  useEffect(saveOriginalDescription, [saveOriginalDescription]);

  const {
    mutate,
    reset,
    isLoading,
    isError: hasError,
  } = useEditDescription({
    signupID,
    onSuccess: (res) => {
      dispatch({ type: "Update Signup Description", value });
      setOriginalDescription(value);
      setHasSavedOriginalDescription(false);
      closeEditor();
      setIsSuccess(true);
    },
  });

  const closeEditor = useCallback(() => {
    editorRef.current.blur();
    reset();
  }, [reset]);

  const submitDescription = useCallback(() => {
    const isOnlyHtml = removeHtmlTags(value)?.trim()?.length === 0;

    if (isOnlyHtml) {
      mutate("");
      setValue("");
    } else {
      mutate(value);
    }

    resetOriginalDescription();
  }, [resetOriginalDescription, mutate, value]);

  const onCancel = () => {
    setValue(originalDescription);
    editor.commands.setContent(originalDescription);
    closeEditor();
  };

  return (
    <DescriptionWrapper>
      <AnimatePresence initial={false}>
        <DescriptionEditor
          editorRef={editorRef}
          isLoading={isLoading}
          hasError={hasError}
          value={value}
          setValue={setValue}
          submitDescription={submitDescription}
          onCancel={onCancel}
          maxCharacters={1500}
          autofocus={false}
          savedSuccessfully={isSuccess}
          lightMode={lightMode}
          editor={editor}
        />
      </AnimatePresence>
    </DescriptionWrapper>
  );
}

Description.propTypes = {
  lightMode: bool,
};

const DescriptionWrapper = styled.div`
  margin: 0 auto;
`;
